import { contentChild, EnvironmentInjector } from '@angular/core';
import { ReferenceDataService } from '../services/reference-data.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DateTime } from 'luxon';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { validityMap, ValidityStatus } from '../models/enums';
import { Affiliation } from '../models/affiliation';
import { Address } from '../models/address';

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'd/M/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

export function extractErrorMessage(err: unknown): string {
  console.debug('error', err)
  if (err instanceof HttpErrorResponse) {
    const problem = err.error;
    return problem?.detail || problem?.title || `[${err.status}] Unexpected error`;
  }
  return 'Unexpected error';
}


export function initializeApp(injector: EnvironmentInjector): void {

  const refData = injector.get(ReferenceDataService);

  refData.refreshAll(true).subscribe({
    next: () => {
      console.info('✅ Reference data initialized');
    },
    error: err => {
      console.error('❌ Reference data initialization failed', err);
    },
    complete: () => {
      console.info('✅ Reference data loading complete');
    }
  });
}


export function normalizeDate(value: unknown): string | null {
  if (!value) return null;

  if (typeof value === 'string') {
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt.toISODate()! : '';
  }

  if (DateTime.isDateTime(value)) {
    return value.isValid ? value.toISODate()! : '';
  }

  return null;
}

/**
 * converts a value into a Luxon date, if possible
 * @param value 
 * @returns 
 */
export function normalizeDateTime(value: unknown): DateTime | null {
  if (!value) return null;

  // Already a Luxon DateTime
  if (DateTime.isDateTime(value)) {
    return value.isValid ? value : null;
  }

  // ISO string from backend
  if (typeof value === 'string') {
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt : null;
  }

  // Native JS Date
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value);
    return dt.isValid ? dt : null;
  }

  return null;
}

/**
 * Excel does not accept Luxon Dates
 * @param value 
 * @returns 
 */
function toExcelDate(value: any): Date | null {
  if (!value) return null;

  // Luxon DateTime
  if (typeof value === 'object' && typeof value.toJSDate === 'function') {
    return value.toJSDate();
  }

  // ISO string
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // Already a Date
  if (value instanceof Date) {
    return value;
  }

  return null;
}


/**
 * 
 * @param value : can be be anything
 * @returns null if value can be transformed in a date; true if date is in the past, false otherwise
 */
export function isPastDate(value: unknown): boolean | null {
  const today = DateTime.now();
  if (!value) return null;

  // Already a Luxon DateTime
  if (DateTime.isDateTime(value)) {
    return value.isValid ? (value.endOf('day') < today ? true : false) : null;
  }

  // ISO string from backend
  if (typeof value === 'string') {
    const dt = DateTime.fromISO(value);
    return dt.isValid ? (dt.endOf('day') < today ? true : false) : null;
  }

  // Native JS Date
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value);
    return dt.isValid ? (dt.endOf('day') < today ? true : false) : null;
  }

  return null;
}

/*
 */

export interface GroupedOptions<T> {
  flat: T[];
  grouped: { label: string; items: T[] }[];
}

export function groupHierarchically<T extends { name: string; categoryId: number | null; categoryName: string | null }>(
  items: T[], sort: boolean
): GroupedOptions<T> {
  const flat = items
    .filter(i => i.categoryId === null)
    .sort((a, b) => a.name.localeCompare(b.name));

  const groupedMap = new Map<string, T[]>();

  items
    .filter(i => i.categoryId !== null)
    .forEach(item => {
      const label = item.categoryName ?? 'Other';
      if (!groupedMap.has(label)) groupedMap.set(label, []);
      groupedMap.get(label)!.push(item);
    });

  const grouped = Array.from(groupedMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, items]) => ({
      label,
      items: items.sort((a, b) => a.name.localeCompare(b.name))
    }));

  return { flat, grouped };
}

/**
 * Validate a URL string
 * @returns 
 */

export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null; // empty is valid; use Validators.required if needed
    }

    // Add protocol if missing so URL() can parse it
    const normalized = value.match(/^https?:\/\//i)
      ? value
      : `https://${value}`;

    try {
      const url = new URL(normalized);
      // Require at least one dot in the hostname 
      if (!url.hostname.includes('.')) {
        return { invalidUrl: true };
      }
      // Reject hostnames with invalid characters 
      if (!/^[a-zA-Z0-9.-]+$/.test(url.hostname)) {
        return { invalidUrl: true };
      }
      return null;
    } catch {
      return { invalidUrl: true };
    }
  };
}

/**
 * Computes the grid columns width according to the type of columns
 */

export function getGridTemplate(selectedColumns: string[], allColumns: any[]): string {
  return selectedColumns
    .map(colKey => {
      const col = allColumns.find(c => c.key === colKey);

      if (!col) return '1fr';

      switch (col.type) {
        case 'longtext':
          return 'minmax(300px, 350px)';
        case 'single-chip':
          return 'minmax(75px,1fr)';
        case 'chips':
          return '1fr';
        case 'icon':
          return 'minmax(45px,45px)';
        case 'text':
        default:
          return 'minmax(100px, 130px)'
      }
    })
    .join(' ');
}


/**
 * Compute the Jaro–Winkler similarity between two strings.
 * @param s1 First string
 * @param s2 Second string
 * @param scalingFactor Winkler scaling factor (default 0.1, max 0.25)
 */
export function jaroWinklerDistance(
  s1: string,
  s2: string,
  scalingFactor = 0.1,
  caseInsensitive: boolean = true
): number {
  if (caseInsensitive) {
    s1 = s1.toLocaleLowerCase();
    s2 = s2.toLocaleLowerCase();
  }

  if (s1 === s2) {
    return 1;
  }

  const len1 = s1.length;
  const len2 = s2.length;

  if (len1 === 0 || len2 === 0) {
    return 0;
  }

  const matchDistance = Math.floor(Math.max(len1, len2) / 2) - 1;

  const s1Matches = new Array<boolean>(len1).fill(false);
  const s2Matches = new Array<boolean>(len2).fill(false);

  let matches = 0;

  // Count matches
  for (let i = 0; i < len1; i++) {
    const start = Math.max(0, i - matchDistance);
    const end = Math.min(i + matchDistance + 1, len2);

    for (let j = start; j < end; j++) {
      if (s2Matches[j]) continue;
      if (s1[i] !== s2[j]) continue;

      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }

  if (matches === 0) {
    return 0;
  }

  // Count transpositions
  let k = 0;
  let transpositions = 0;

  for (let i = 0; i < len1; i++) {
    if (!s1Matches[i]) continue;

    while (!s2Matches[k]) {
      k++;
    }

    if (s1[i] !== s2[k]) {
      transpositions++;
    }
    k++;
  }

  transpositions /= 2;

  const jaro =
    (matches / len1 +
      matches / len2 +
      (matches - transpositions) / matches) / 3;

  // Winkler prefix boost (max 4 chars)
  let prefixLength = 0;
  const maxPrefix = 4;

  for (
    let i = 0;
    i < Math.min(maxPrefix, len1, len2);
    i++
  ) {
    if (s1[i] === s2[i]) {
      prefixLength++;
    } else {
      break;
    }
  }

  const p = Math.min(scalingFactor, 0.25);

  return jaro + prefixLength * p * (1 - jaro);
}

/**
 * map an object by selecting only the field names present in fields
 * @param data 
 * @param fields 
 * @returns 
 */
export function mapObject<T extends Record<string, any>>(item: T, fields: string[]) {
  const result: any = {};
  for (const field of fields) {
    const value = item[field];
    switch (field) {
      case 'lastModifiedOn':
        result[field] = toExcelDate(value);
        break;
      case 'valid':
        result[field] = validityMap[item[field] as ValidityStatus] ?? 'N/A';
        break;
      default:
        if (Array.isArray(value)) {
          // Array of objects with a "name" property
          if (value.length > 0 && typeof value[0] === 'object' && 'name' in value[0]) {
            result[field] = value.map(v => v.name).join(', ');
          } else {
            // Array of primitives
            result[field] = value.join(', ');
          }
        } else {
          if (typeof value === 'boolean') {
            result[field] = (value ? 'Yes': 'No');
          } else {
          result[field] = value;
          }
        }
    }
  }
  return result;
}

// compares two arrays of elements that have an id field

export function haveSameIds<A extends { id: string | number }, B extends { id: string | number }>(
  a: A[] | null | undefined,
  b: B[] | null | undefined
): boolean {

  const idsA = new Set((a ?? []).map(v => v.id));
  const idsB = new Set((b ?? []).map(v => v.id));

  if (idsA.size !== idsB.size) {
    return false;
  }

  return [...idsA].every(id => idsB.has(id));
}

// Capitalize first letter
export function capitalizeFirstLetter(word: string | null): string | null {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}
